import { LEASE_STATUS_COLOR_ENUM, LEASE_STATUS_ENUM } from '@modules/private/lease/data/enum/lease.enum'

/** Las vistas de Comités. Cada una es una RUTA, no estado en memoria (molde: Cobranza). */
export type CommitteeView = 'sesiones' | 'por-originar' | 'activos' | 'cerrados' | 'sesion'

/** Los tres grupos de arrendamientos que sirve `GET /committee/sessions/leases?group=`. */
export type LeaseGroup = 'originating' | 'active' | 'closed'

/** La lente de las tablas de arrendamientos: todos, o solo los que no tienen comité decidido. */
export type LeaseLens = 'all' | 'without-committee'

/**
 * Qué grupo del contrato sirve cada vista con tabla. Las rutas se quedan en español (convención del
 * repo); a la API viaja el valor del contrato.
 */
export const VIEW_GROUP: Partial<Record<CommitteeView, LeaseGroup>> = {
  'por-originar': 'originating',
  activos: 'active',
  cerrados: 'closed',
}

/** Lo que hace falta para pintar y gatear una vista. */
export interface ViewDef {
  key: CommitteeView
  label: string
  icon: string
  path: string
  /** El permiso que la abre. Cada vista lleva el suyo en su propia ruta. */
  perm: [string, string]
}

/**
 * Las cuatro pestañas del segmentado. Todas con `committee.view`: en `roles.json` lo tienen Admin,
 * Legal, Operaciones y Finanzas, y la diferencia entre roles está en las ACCIONES (votar, cerrar,
 * crear), no en qué pestaña pueden mirar (D-7).
 */
export const COMMITTEE_VIEWS: ViewDef[] = [
  { key: 'sesiones', label: 'Sesiones', icon: 'fa-solid fa-gavel', path: '/comites/sesiones', perm: ['committee', 'view'] },
  { key: 'por-originar', label: 'Por originar', icon: 'fa-solid fa-hourglass-half', path: '/comites/por-originar', perm: ['committee', 'view'] },
  { key: 'activos', label: 'Activos', icon: 'fa-solid fa-file-signature', path: '/comites/activos', perm: ['committee', 'view'] },
  { key: 'cerrados', label: 'Cerrados', icon: 'fa-solid fa-box-archive', path: '/comites/cerrados', perm: ['committee', 'view'] },
]

//* 30 · 60 · 90 para una cartera de ~260 arrendamientos: el grupo más grande cabe en tres páginas y el
//* más chico en una. Son de este módulo; que coincidan con otra tabla del panel no es una dependencia.
export const COMMITTEE_PAGE_SIZES = [30, 60, 90]
export const COMMITTEE_PAGE_SIZE = 30

/** Cuánto se espera a que deje de teclear antes de consultar. */
export const COMMITTEE_SEARCH_DEBOUNCE_MS = 350

/** El tono de una `.pt-pill` (`_committee-ui.scss`). */
export type PillTone = 'ok' | 'warn' | 'info' | 'bad' | 'muted'

//* Los mapas son `Partial`: un estado que la base traiga y el panel no conozca no debe tirar la fila;
//* por eso las plantillas los leen con `?.` y caen a un tono neutro.
export interface PillDef {
  label: string
  tone: PillTone
}

/**
 * De la severidad de `p-tag` —el vocabulario del resto del panel y de `LEASE_STATUS_COLOR_ENUM`— al
 * tono de la píldora, y de vuelta para la pestaña del expediente, que sigue pintando con `p-tag`.
 * `primary` no tiene tono propio y se lee como informativo; `muted` no existe en `p-tag` 16 y cae a `info`.
 */
export const SEVERITY_TONE: Record<string, PillTone> = { success: 'ok', warning: 'warn', info: 'info', danger: 'bad', primary: 'info' }
export const TONE_SEVERITY: Record<PillTone, string> = { ok: 'success', warn: 'warning', info: 'info', bad: 'danger', muted: 'info' }

/**
 * Cómo se lee cada estado de reunión, y de qué color va. «En revisión» va en `info` aquí y en el
 * estado del comité: es un estado intermedio, no una alerta.
 */
export const MEETING_STATUS_PILL: Partial<Record<string, PillDef>> = {
  Open: { label: 'Abierta', tone: 'info' },
  Aprobado: { label: 'Aprobado', tone: 'ok' },
  Rechazado: { label: 'Rechazado', tone: 'bad' },
  Revision: { label: 'En revisión', tone: 'info' },
}

/**
 * El estado del comité del arrendamiento (`overallStatus`). La pestaña Comité del expediente lo
 * consume también: es EL mapa, no una copia.
 */
export const COMMITTEE_STATUS_PILL: Partial<Record<string, PillDef>> = {
  Pendiente: { label: 'Pendiente', tone: 'warn' },
  En_Revision: { label: 'En revisión', tone: 'info' },
  Aprobado: { label: 'Aprobado', tone: 'ok' },
  Rechazado: { label: 'Rechazado', tone: 'bad' },
}

/**
 * El estado del arrendamiento, con la etiqueta y el color del canon del módulo de arrendamientos
 * (`LEASE_STATUS_ENUM` / `LEASE_STATUS_COLOR_ENUM`). Aquí no se declara ningún color de estado de
 * arrendamiento: si el canon cambia, esto cambia con él.
 */
export const LEASE_STATUS_PILL: Partial<Record<string, PillDef>> = Object.fromEntries(
  (Object.values(LEASE_STATUS_ENUM) as string[]).map((status) => [status, { label: status, tone: SEVERITY_TONE[LEASE_STATUS_COLOR_ENUM[status as keyof typeof LEASE_STATUS_COLOR_ENUM]] ?? 'muted' }]),
)

export const SESSION_KIND_LABEL: Record<string, string> = {
  voting: 'Votación',
  documentary: 'Registro de acta',
}

export const SESSION_STATUS_PILL: Partial<Record<string, PillDef>> = {
  open: { label: 'Abierta', tone: 'info' },
  closed: { label: 'Cerrada', tone: 'muted' },
}

/** Las tres salidas que Legal puede dictaminar a mano al cerrar una reunión sin votos con quórum (etiquetas de `MEETING_STATUS_PILL`). */
export const MANUAL_DECISION_OPTIONS: { label: string; value: 'Aprobado' | 'Rechazado' | 'Revision' }[] = [
  { label: 'Aprobado', value: 'Aprobado' },
  { label: 'Rechazado', value: 'Rechazado' },
  { label: 'En revisión', value: 'Revision' },
]
